import { HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Observable } from "rxjs";
import { ArquivoService } from "src/app/services/arquivo.service";
import { UtilsService } from "src/app/utils/utils.service";

export interface FileDownloadBackgound {
  id: number;
  name: string;
  success: boolean;
  error: boolean;
  done: boolean;
}

export interface FileDownloadProgress {
  fileId: number;
  progress: number;
  indeterminate: boolean;
}

@Injectable({
  providedIn: "root",
})
export class FileDownloadBackgoundService {
  public showDownloadInBackgroud: BehaviorSubject<{
    show: boolean;
    files: Map<number, FileDownloadBackgound>;
  }> = new BehaviorSubject({ show: false, files: new Map() });

  private files: Map<number, FileDownloadBackgound> = new Map();
  private downloadQueue: Array<{ arquivoId: any; file: any }> = [];
  private processandoDownload = false;
  private exportacaoTimeouts: any[] = [];

  public downloadProgress$ = new BehaviorSubject<Map<number, FileDownloadProgress>>(new Map());
  private downloadProgressMap = new Map<number, FileDownloadProgress>();

  constructor(
    private toastrService: ToastrService,
    private utilsService: UtilsService,
    private arquivoService: ArquivoService
  ) {}

  show({ arquivoId, file }) {
    this.files.set(file.id, {
      id: file.id,
      name: file.name,
      success: false,
      error: false,
      done: false,
    });

    this.showDownloadInBackgroud.next({ show: true, files: this.files });
    this.downloadProgressMap.set(file.id, { fileId: file.id, progress: 0, indeterminate: true });
    this.downloadProgress$.next(this.downloadProgressMap);

    this.downloadQueue.push({ arquivoId, file });
    this.processarFila();
  }
  showAsyncUrl({ file, start, status }: { file: any; start: () => Observable<any>; status: (exportacao: any) => Observable<any> }) {
    this.files.set(file.id, {
      id: file.id,
      name: file.name,
      success: false,
      error: false,
      done: false,
    });

    this.showDownloadInBackgroud.next({ show: true, files: this.files });
    this.downloadProgressMap.set(file.id, { fileId: file.id, progress: 0, indeterminate: true });
    this.downloadProgress$.next(this.downloadProgressMap);

    start().subscribe(
      (response: any) => {
        if (!response || !response.success || !response.data) {
          this.downloadErrorComplete(file, false);
          return;
        }

        this.acompanharExportacao(file, response.data, status);
      },
      () => this.downloadErrorComplete(file, false)
    );
  }

  private acompanharExportacao(file: any, exportacao: any, status: (exportacao: any) => Observable<any>, tentativa = 0) {
    status(exportacao).subscribe(
      (response: any) => {
        const data = response && response.data;

        if (response && response.success && data && data.status === "Concluido" && data.urlPreAssinada) {
          this.downloadProgressMap.set(file.id, { fileId: file.id, progress: 100, indeterminate: false });
          this.downloadProgress$.next(this.downloadProgressMap);
          this.renderDownloadUrl(data.urlPreAssinada, data.nomeArquivo || file.name, data.contentType);
          this.downloadErrorComplete(file, true);
          return;
        }

        if (data && (data.status === "Falhou" || data.status === "ArquivoNaoEncontrado" || data.status === "NaoEncontrado")) {
          this.toastrService.error(data.erro || "Nao foi possivel gerar o relatorio.", "Atencao", { timeOut: 10000 });
          this.downloadErrorComplete(file, false);
          return;
        }

        if (tentativa >= 720) {
          this.toastrService.error("Tempo limite excedido ao gerar o relatorio.", "Atencao", { timeOut: 10000 });
          this.downloadErrorComplete(file, false);
          return;
        }

        const timeoutId = setTimeout(() => this.acompanharExportacao(file, exportacao, status, tentativa + 1), 5000);
        this.exportacaoTimeouts.push(timeoutId);
      },
      () => {
        this.downloadErrorComplete(file, false);
      }
    );
  }
  private processarFila() {
    if (this.processandoDownload) {
      return;
    }

    const item = this.downloadQueue.shift();
    if (!item) {
      this.processandoDownload = false;
      return;
    }

    this.processandoDownload = true;
    const { arquivoId, file } = item;
    const janelaPdf = this.isPdf(file.name) ? window.open("", "_blank") : null;
    if (janelaPdf) {
      janelaPdf.opener = null;
    }

    this.arquivoService.gerarUrlPreAssinada(arquivoId).subscribe(
      (response: any) => {
        if (response && response.success && response.data && response.data.urlPreAssinada) {
          this.downloadProgressMap.set(file.id, { fileId: file.id, progress: 100, indeterminate: false });
          this.downloadProgress$.next(this.downloadProgressMap);
          this.renderDownloadUrl(response.data.urlPreAssinada, response.data.nome || file.name, response.data.contentType, janelaPdf);
          this.downloadErrorComplete(file, true);
          this.agendarProximoDownload();
          return;
        }

        this.downloadArquivoViaApi(arquivoId, file, janelaPdf, () => this.agendarProximoDownload());
      },
      () => this.downloadArquivoViaApi(arquivoId, file, janelaPdf, () => this.agendarProximoDownload())
    );
  }

  private downloadArquivoViaApi(arquivoId, file, janelaPdf?: Window, aoConcluir?: () => void) {
    this.fecharJanelaPendente(janelaPdf);
    this.downloadProgressMap.set(file.id, { fileId: file.id, progress: 0, indeterminate: false });
    this.downloadProgress$.next(this.downloadProgressMap);

    this.arquivoService.downloadArquivo(arquivoId).subscribe(
      (res) => {
        if (res.type === HttpEventType.DownloadProgress) {
          const hasTotal = !!res.total && res.total > 0;
          const progress = hasTotal ? Math.round((100 * res.loaded) / res.total) : 0;

          this.downloadProgressMap.set(file.id, { fileId: file.id, progress, indeterminate: !hasTotal });

          this.downloadProgress$.next(this.downloadProgressMap);
        }

        if (res.type === HttpEventType.Response) {
          this.downloadProgressMap.set(file.id, { fileId: file.id, progress: 100, indeterminate: false });
          this.downloadProgress$.next(this.downloadProgressMap);
          this.utilsService.renderDownload(res, file.name as string);
          this.downloadErrorComplete(file, true);
          if (aoConcluir) {
            aoConcluir();
          }
        }
      },
      (error) => {
        if (error && error.errors)
          this.toastrService.error(error.errors[0], "Atenção", {
            timeOut: 10000,
          });
        else
          this.toastrService.error(
            "Não foi possível fazer o download do documento!",
            "Atenção",
            { timeOut: 10000 }
          );

        this.downloadErrorComplete(file, false);
        if (aoConcluir) {
          aoConcluir();
        }
      }
    );
  }

  private agendarProximoDownload() {
    setTimeout(() => {
      this.processandoDownload = false;
      this.processarFila();
    }, 1200);
  }

  private renderDownloadUrl(url: string, fileName: string, contentType?: string, janelaPdf?: Window) {
    if (this.isPdf(fileName, contentType)) {
      if (janelaPdf) {
        janelaPdf.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
      return;
    }

    this.fecharJanelaPendente(janelaPdf);

    const a = document.createElement("a");
    a.setAttribute("style", "display:none;");
    document.body.appendChild(a);
    a.download = fileName;
    a.href = url;
    a.click();
    document.body.removeChild(a);
  }

  private isPdf(fileName: string, contentType?: string): boolean {
    return contentType === "application/pdf" || (fileName || "").toLowerCase().endsWith(".pdf");
  }

  private fecharJanelaPendente(janelaPdf?: Window) {
    if (janelaPdf && !janelaPdf.closed) {
      janelaPdf.close();
    }
  }

  private downloadErrorComplete(file, success: boolean) {
    this.files.forEach((f, i) => {
      if (f.id === file.id) {
        this.files.set(i, {
          ...f,
          error: !success,
          success,
          done: true,
        });
      }
    });

    this.showDownloadInBackgroud.next({ show: true, files: this.files });
  }

  hide() {
    this.files = new Map();
    this.downloadQueue = [];
    this.exportacaoTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.exportacaoTimeouts = [];
    this.processandoDownload = false;
    this.downloadProgressMap = new Map();
    this.downloadProgress$.next(this.downloadProgressMap);
    this.showDownloadInBackgroud.next({ show: false, files: this.files });
  }
}
